import FormControlLabel from "@mui/material/FormControlLabel";
import { TooltipProps } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { commonClasses, FIGMA_COLORS } from "components-redesign/styles";
import { Tooltip } from "components-redesign/Tooltip/Tooltip";
import { Switch as MuiSwitch, makeStyles } from "components/mui-core";
import React, { ChangeEvent } from "react";

const useCommonStyle = makeStyles()(commonClasses as any);
const useStyles = makeStyles()({
  labelActive: {
    color: FIGMA_COLORS.shadowLight,
  },
});

const NO_ICON_THUMB_STYLE = { width: 16, height: 16, margin: 4 };
const ICON_THUMB_STYLE = {
  width: 24,
  height: 24,
  margin: 0,
  backgroundImage: `url('${process.env.PUBLIC_URL}/assets/close.svg')`,
};
const X_THUMB_CHECKED_STYLE = {
  backgroundImage: `url('${process.env.PUBLIC_URL}/assets/check.svg')`,
};

const StyledSwitch = styled((props: any) => (
  <MuiSwitch
    focusVisibleClassName=".Mui-focusVisible"
    disableRipple
    {...props}
  />
))(({ variant }: { variant?: "icon-thumb" | "no-icon-thumb" }) => ({
  width: 52,
  height: 32,
  padding: 0,
  borderRadius: 100,
  marginInlineEnd: 12,
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    backgroundColor: "#79747E",
    ...(variant === "icon-thumb" && ICON_THUMB_STYLE),
    ...(variant === "no-icon-thumb" && NO_ICON_THUMB_STYLE),
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    boxShadow: "none",
  },
  "& .MuiSwitch-switchBase": {
    padding: 1,
    margin: "2px 4px",
    top: 1,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(18px)",
      color: FIGMA_COLORS.onPrimaryLight,
      "& + .MuiSwitch-track": {
        backgroundColor: "#65558F",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
      "& .MuiSwitch-thumb": {
        margin: 0,
        width: 24,
        height: 24,
        backgroundColor: FIGMA_COLORS.onPrimaryLight,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        ...(variant === "icon-thumb" && X_THUMB_CHECKED_STYLE),
      },
    },
  },
  "& .MuiSwitch-track": {
    borderRadius: 100,
    border: "2px solid #79747E",
    backgroundColor: "#E6E0E9",
    opacity: 1,
    transition: "background-color 300ms linear",
  },
}));

type IProps = {
  label: string | React.ReactNode;
  checked: boolean | undefined;
  onChange: (val: boolean) => void;
  classes?: {
    label?: string;
    container?: string;
  };
  tooltip?: string;
  disabled?: boolean;
  variant?: "icon-thumb" | "no-icon-thumb";
  dataQa?: string;
} & Pick<TooltipProps, "placement">;

const Switch: React.FC<IProps> = ({
  label,
  checked,
  classes,
  onChange,
  tooltip,
  placement,
  disabled,
  variant = "icon-thumb",
  dataQa,
}) => {
  const { classes: inputClasses, cx } = useStyles();
  const { classes: commonClasses } = useCommonStyle();

  return (
    <Tooltip
      placement={placement}
      title={tooltip || ""}>
      <FormControlLabel
        label={label}
        sx={{ marginLeft: "0 !important" }}
        classes={{
          root: classes?.container,
          label: cx(
            commonClasses.label,
            checked && inputClasses.labelActive,
            classes?.label
          ),
        }}
        control={
          <StyledSwitch
            variant={variant}
            tooltip={tooltip}
            checked={checked}
            onChange={(_e: ChangeEvent<HTMLInputElement>, checked: boolean) =>
              onChange(checked)
            }
            disabled={disabled}
            inputProps={dataQa ? ({ "data-qa": dataQa } as object) : undefined}
          />
        }
      />
    </Tooltip>
  );
};

export default Switch;
